import { Send } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Textarea } from '../ui.tsx';
import { StateView } from './StateView.tsx';

export interface ConversationThread {
  id: string;
  title: ReactNode;
  preview?: ReactNode;
  /** Quantas mensagens não lidas. */
  unread?: number;
}

export interface ConversationMessage {
  id: string;
  /** Quem escreveu, já formatado. */
  author: ReactNode;
  /** Hora ou data, já formatada. */
  when: ReactNode;
  text: ReactNode;
  /** Mensagem de quem está usando: fica à direita, no tom do destaque. */
  mine?: boolean;
}

/**
 * Conversas (ADR 0128): as conversas à esquerda, as mensagens da aberta e o campo de responder.
 * No celular, a lista e a conversa aberta se revezam.
 */
export function ConversationLayout({
  threads,
  openId,
  onOpen,
  messages,
  onSend,
  header,
}: {
  threads: readonly ConversationThread[];
  openId: string | null;
  onOpen: (id: string | null) => void;
  messages: readonly ConversationMessage[];
  onSend: (text: string) => void;
  /** Topo da conversa aberta (nome, situação, botões). */
  header?: ReactNode;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const send = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    onSend(text);
    setDraft('');
  };
  return (
    <div className="ui-panel grid min-h-[28rem] overflow-hidden rounded-lg bg-sheet md:grid-cols-[18rem_minmax(0,1fr)]">
      <nav
        aria-label={t('patterns.conversation.threads')}
        className={`flex flex-col border-rule md:border-r ${openId ? 'max-md:hidden' : ''}`}
      >
        {threads.length === 0 ? (
          <StateView kind="empty" compact title={t('patterns.conversation.none')} />
        ) : (
          <ul className="flex flex-col">
            {threads.map((th) => (
              <li key={th.id}>
                <button
                  type="button"
                  onClick={() => onOpen(th.id)}
                  aria-current={th.id === openId ? 'true' : undefined}
                  className={`flex w-full items-start gap-2 border-b border-rule px-4 py-3 text-left text-sm ${
                    th.id === openId ? 'bg-brand-soft' : 'hover:bg-paper'
                  }`}
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate font-medium">{th.title}</span>
                    {th.preview && (
                      <span className="truncate text-xs text-muted">{th.preview}</span>
                    )}
                  </span>
                  {!!th.unread && (
                    <span className="rounded-full bg-accent px-1.5 text-xs font-semibold text-on-accent tabular-nums">
                      {th.unread}
                      <span className="sr-only"> {t('patterns.conversation.unread')}</span>
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </nav>
      <section className={`flex min-w-0 flex-col ${openId ? '' : 'max-md:hidden'}`}>
        {openId ? (
          <>
            <header className="flex items-center gap-2 border-b border-rule px-4 py-2.5">
              <button
                type="button"
                onClick={() => onOpen(null)}
                className="text-sm text-muted hover:text-ink md:hidden"
              >
                {t('patterns.conversation.back')}
              </button>
              <div className="min-w-0 flex-1">{header}</div>
            </header>
            <ol
              aria-live="polite"
              className="flex flex-1 flex-col gap-3 overflow-y-auto bg-paper/50 p-4"
            >
              {messages.map((m) => (
                <li
                  key={m.id}
                  className={`flex max-w-[80%] flex-col gap-1 ${m.mine ? 'self-end items-end' : 'self-start'}`}
                >
                  <span className="text-xs text-muted">
                    {m.author} · {m.when}
                  </span>
                  <div
                    className={`rounded-lg px-3 py-2 text-sm ${
                      m.mine ? 'bg-accent text-on-accent' : 'ui-panel bg-sheet'
                    }`}
                  >
                    {m.text}
                  </div>
                </li>
              ))}
            </ol>
            <form onSubmit={send} className="flex items-end gap-2 border-t border-rule p-3">
              <Textarea
                rows={2}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                aria-label={t('patterns.conversation.reply')}
                placeholder={t('patterns.conversation.reply')}
                className="min-h-0 flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) send(e);
                }}
              />
              <Button type="submit" disabled={!draft.trim()}>
                <Send size={16} aria-hidden />
                {t('patterns.conversation.send')}
              </Button>
            </form>
          </>
        ) : (
          <StateView kind="empty" title={t('patterns.conversation.pick')} />
        )}
      </section>
    </div>
  );
}
